import React, { useEffect, useRef } from 'react';
import IMask from 'imask';
import Input from '../html/Input';

const DateOfBirthInput = ({ value, disabled, required, onChange }) => {
  const inputRef = useRef();
  const maskRef = useRef();
  const valueRef = useRef(value);
  const internalChangeRef = useRef(false);

  useEffect(() => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    const today = new Date();

    const mask = IMask(input, {
      mask: Date,
      pattern: 'm/`d/`Y',
      format: date => {
        let day = date.getDate();
        let month = date.getMonth() + 1;
        const year = date.getFullYear();

        if (day < 10) {
          day = '0' + day;
        }

        if (month < 10) {
          month = '0' + month;
        }

        return [month, day, year].join('/');
      },
      parse: str => {
        const [month, day, year] = str.split('/');
        return new Date(year, month - 1, day);
      },
      lazy: false,
    });

    syncValue(mask, valueRef.current);

    maskRef.current = mask;

    const regex = /(\d{2})\/(\d{2})\/(\d{4})/;

    const handleAccept = () => {
      internalChangeRef.current = true;

      setTimeout(() => {
        internalChangeRef.current = false;
      }, 0);
    };

    const handleComplete = () => {
      const raw = mask.unmaskedValue || '';
      if (raw.length === 8) {
        const month = raw.slice(0, 2);
        const day = raw.slice(2, 4);
        const year = raw.slice(4, 8);
        onChange(`${year}-${month}-${day}`);
        return;
      }

      const match = mask.value && mask.value.match(regex);
      if (match) {
        const [, month, day, year] = match;
        onChange(`${year}-${month}-${day}`);
      }
    };

    const handleBlur = () => {
      // Do not clear user input on blur; leave partial input so user can fix it.
      // We only emit a value on complete; incomplete input stays in the field.
    };

    mask.on('accept', handleAccept);
    mask.on('complete', handleComplete);

    input.addEventListener('blur', handleBlur);

    return () => {
      input.removeEventListener('blur', handleBlur);
      mask.off('complete', handleComplete);
      mask.off('accept', handleAccept);
      mask.destroy();
    };
  }, [onChange]);

  useEffect(() => {
    const mask = maskRef.current;

    if (!mask) {
      return;
    }

    if (internalChangeRef.current) {
      return;
    }

    syncValue(mask, value);
  }, [value]);

  const complete = value?.match(/(\d{4})-(\d{2})-(\d{2})/);

  useEffect(() => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    input.setCustomValidity(required && !complete ? 'Date of birth is required' : '');
  }, [required, complete]);

  return (
    <Input
      ref={inputRef}
      autoComplete="on"
      autoCapitalize="off"
      autoCorrect="off"
      disabled={disabled}
      required={required}
    />
  );
};

export default DateOfBirthInput;

function syncValue(mask, value) {
  if (!value) {
    return;
  }

  const match = value.match(/(\d{4})-(\d{2})-(\d{2})/);

  if (match) {
    const [, year, month, day] = match;

    const unmaskedValue = `${month}${day}${year}`;

    if (unmaskedValue !== mask.unmaskedValue) {
      mask.unmaskedValue = unmaskedValue;
    }
  }
}
